import _ = require("lodash");
import { ReportsRepo } from "@services/reports.repo";
import { ReportingService } from "@common/reporting.service";
import { AuthService } from "@common/auth.service";
import { EmailStatus } from "@models/portfolioadmin/reportManagement/emailStatus";
import { Roles } from "@common/enums/Roles";
import moment = require("moment");
import { ReportingParams } from "@app/reporting/reportingParams";
import { ReportDetails } from "@models/portfolioadmin/reportManagement/reportDetails";
import { extractErrorMessage } from "@common/utils/errorUtils";

class PortfolioAttributionReportController {
  public client: any;
  public clientId: number;
  public selectedPortfolioIds: any;
  public selectedCurrency: any;
  public clientPortfolios: any;
  public clientNumber: any;
  
  public reportTypes: Array<string> = ["portfolioattribution"];
  public isLoading: boolean = false;
  public documentGenerating: boolean;
  public isLoadingReports: boolean;
  public reports: any = {
    portfolioattribution: []
  };
  public reportingParams: ReportingParams = new ReportingParams();
  public reportDate: any;
  public currentDate: Date;

  /** @ngInject */

  constructor(
    private $scope: angular.IScope,
    private notifyjs: any,
    private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService,
    private reportsRepo: ReportsRepo,
    private reportingService: ReportingService,
    private authService: AuthService,
    private $stateParams: angular.ui.IStateParamsService,
  ) {}

  $onInit() {
    this.clientNumber = this.$stateParams["clientNumber"]
    this.getReportsForReportType(this.clientNumber, "portfolioattribution");
  }

  getClientReports = () => {
    this.getReportsForReportType(this.clientNumber, "portfolioattribution");
  };

  private getReportsForReportType(clientNumber, reportType) {
    this.isLoading = true;
    return this.reportsRepo
      .getClientReportByType(clientNumber, reportType)
      .then((reportDetails) => {
        this.generateReportDisplayCards(reportDetails, reportType);
      })
      .catch((err) =>
        this.notifyjs(
          "The reports could not be loaded. Please try again later or contact the System Administrator.",
          "error"
        )
      )
      .finally(() => (this.isLoading = false));
  }
  generateReportDisplayCards(
    reportDetails: ReportDetails[],
    reportType: string
  ) {
    this.reports[reportType] = [];
    _.forEach(reportDetails, (report) => {
      const displayDate = moment(report.reportDate).format("MMMM Y");
      let currentReport = _.find(
        this.reports[reportType],
        (r) => r["date"] == displayDate
      );

      if (!currentReport) {
        currentReport = {
          date: displayDate,
          type: reportType,
          isLocked: report.isLocked,
          isReconciled: report.isReconciled,
          reports: [],
          custodianReports: report.custodianReports,
        };
        this.reports[reportType].push(currentReport);
      }

      const reportDetails = {
        reportId: report.reportId,
        bucket: report.bucketName,
        reportName: "",
        key: report.reportKey,
        emailStatuses: report.emailStatuses,
      };

      if (report.externalSource === null) {
        reportDetails.reportName = `Confident (${report.reportCurrency})`;
      } else if (report.externalSource === "MCB") {
        reportDetails.reportName = "MCB";
      } else if (report.externalSource === "Rothschild") {
        reportDetails.reportName = "EDR";
      }

      currentReport.reports.push(reportDetails);
    });
  }

  generateReport = () => {
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.selectedPortfolios = this.selectedPortfolioIds;
    tempScope.selectedCurrency = this.selectedCurrency;
    tempScope.clientNumber = this.clientNumber;
    tempScope.clientPortfolios = this.clientPortfolios.map(
      (portfolio) => portfolio.id
    );
    tempScope.lockedDates = _.map(
      _.flatMap(this.reports, (r) => _.filter(r, (t) => t.isLocked)) as any[],
      (r) => r.date
    );

    this.currentDate = new Date();
    this.reportDate = moment(this.currentDate)//.endOf("month")
      .format("MM/DD/YYYY");
    let fromDate = moment(this.reportDate).startOf("month").format("MM/DD/YYYY");
    this.reportingParams.toDate = this.reportDate;
    this.reportingParams.fromDate = fromDate;
    this.reportingParams.reportType = "portfolioattribution";
    this.reportingParams.clientNumber = this.clientNumber;
    this.reportingParams.userId = null;
    this.reportingParams.portfolioIds = null;
    this.reportingParams.currency = this.selectedCurrency;
    this.reportingParams.portfolioAttribution = true;
    this.reportingService
      .generateReport(this.reportingParams)
      .then((res) => {
        // Silently refresh data in the background
        this.getClientReportsSilently();
      })
      .catch(() =>
        this.notifyjs(
          "The report could not be generated. Please try again later or contact the System Administrator for assistance.",
          "error"
        )
      );
  };

  public getDownloadCountOutput(report: any) {
    let keys = Object.keys(report.reports);
    let count = keys.length;
    if (count === 1) return "1 report";
    else return `${count} reports`;
  }

  public formatToStartCase(word) {
    return _.startCase(word);
  }

  private displayStatus(status: EmailStatus) {
    switch (status) {
      case EmailStatus.PENDING:
        return "Not Sent";
      case EmailStatus.SENDING:
        return "Busy";
      case EmailStatus.OPENED:
        return "Opened";
      case EmailStatus.FAILED:
        return "Failed To Send";
      case EmailStatus.SENT:
      default:
        return "Sent";
    }
  }

  public toggleReportLock(reportDetails) {
    const userRoles = this.authService.getUserRoles();
    if (
      !reportDetails.isLocked &&
      userRoles.indexOf(Roles.SeniorManagement) === -1 &&
      userRoles.indexOf(Roles.SystemAdministrator) === -1
    ) {
      reportDetails.isLocked = true;
      this.notifyjs("Only senior management can unlock a report.", "warning");
      return;
    }

    const reportIds = _.map(reportDetails.reports, (r) => r.reportId);
    this.reportsRepo.toggleReportLock(reportIds, reportDetails.isLocked);
  }

  public emailReport(reportDetails: any) {
    if (
      reportDetails.emailStatuses.every((s) => s.status === EmailStatus.PENDING)
    ) {
      this.reportsRepo
        .sendReportEmails(reportDetails.reportId)
        .then(() =>
          reportDetails.emailStatuses.forEach(
            (s) => (s.status = EmailStatus.SENDING)
          )
        )
        .catch((err) => this.notifyjs(extractErrorMessage(err)));
    } else if (
      reportDetails.emailStatuses.some((s) => s.status === EmailStatus.SENDING)
    ) {
      return;
    } else if (
      reportDetails.emailStatuses.every(
        (s) =>
          s.status === EmailStatus.SENT ||
          s.status === EmailStatus.FAILED ||
          s.status === EmailStatus.OPENED
      )
    ) {
      const dialogScope = this.$scope.$new() as any;
      dialogScope.emails = reportDetails.emailStatuses;

      this.$mdDialog
        .show({
          template:
            '<md-dialog><email-select-dialog available-emails="emails"></email-select-dialog></md-dialog>',
          scope: dialogScope,
        })
        .then((emailIds) =>
          this.reportsRepo
            .resendReportEmailToSpecificClients(reportDetails.reportId, emailIds)
            .catch((err) => this.notifyjs(extractErrorMessage(err)))
        )
        .then(() =>
          reportDetails.emailStatuses.forEach(
            (s) => (s.status = EmailStatus.SENDING)
          )
        )
        .catch((err) => console.log("Cancelled"));
    }
  }
  emailStatusTooltip(report) {}

  deleteClientReport = (clientReport) => {
    // Optimistically remove the report from the UI
    let reportRemoved = false;
    _.forEach(this.reports, (reportArray, reportType) => {
      _.forEach(reportArray, (report) => {
        if (report.reports) {
          const index = report.reports.findIndex(r => r.reportId === clientReport.reportId);
          if (index !== -1) {
            report.reports.splice(index, 1);
            reportRemoved = true;
          }
        }
      });
    });

    // Delete in the background without showing loading state
    this.reportsRepo
      .deleteClientReport(clientReport.reportId)
      .then((res) => {
        // Silently refresh data in the background
        this.getClientReportsSilently();
      })
      .catch((err) => {
        // Revert the optimistic update on error
        if (reportRemoved) {
          this.getClientReports();
        }
        this.notifyjs("Failed to delete custodian report " + extractErrorMessage(err));
      });
  };
  deleteCustodianReport = (custodianReport) => {
    // Optimistically remove the report from the UI
    let reportRemoved = false;
    _.forEach(this.reports, (reportArray, reportType) => {
      _.forEach(reportArray, (report) => {
        if (report.custodianReports) {
          const index = report.custodianReports.findIndex(cr => cr.reportId === custodianReport.reportId);
          if (index !== -1) {
            report.custodianReports.splice(index, 1);
            reportRemoved = true;
          }
        }
      });
    });

    // Delete in the background without showing loading state
    this.reportsRepo
      .deleteCustodianReport(custodianReport.reportId)
      .then((res) => {
        // Silently refresh data in the background
        this.getClientReportsSilently();
      })
      .catch((err) => {
        // Revert the optimistic update on error
        if (reportRemoved) {
          this.getClientReports();
        }
        this.notifyjs("Failed to delete custodian report " + extractErrorMessage(err));
      });
  };

  private getClientReportsSilently = () => {
    // Refresh data without showing loading state
    this.getReportsForReportTypeSilently(this.clientNumber, "portfolioattribution");
  };

  private getReportsForReportTypeSilently(clientNumber, reportType) {
    // Fetch reports without setting isLoading
    return this.reportsRepo
      .getClientReportByType(clientNumber, reportType)
      .then((reportDetails) => {
        this.generateReportDisplayCards(reportDetails, reportType);
      })
      .catch((err) => {
        // Silently handle errors - don't show notification for background refresh
        console.error("Error refreshing reports silently:", err);
      });
  }

  public openReport(reportDetails: any) {
    this.reportingService.openReport(reportDetails.key);
  }
  uploadCustodianReport(report) {
    const tempScope = this.$rootScope.$new(true) as any;

    tempScope.clientPortfolios = this.clientPortfolios.map((portfolio) => {
      return {
        id: portfolio.id,
        name: portfolio.name,
      };
    });

    tempScope.reportDate = report.date;

    this.$mdDialog
      .show({
        template: `<custodian-report-dialog client-portfolios="clientPortfolios" report-date="reportDate"> </custodian-report-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((res) => {
        if (res) {
          // Silently refresh data in the background
          this.getClientReportsSilently();
        }
      });
  }

  public isSending(reportDetails): boolean {
    if (!reportDetails) {
      return true;
    }
    return reportDetails.emailStatuses.some(
      (s) => s.status === EmailStatus.SENDING
    );
  }

  save() {}

  cancel() {
    this.$mdDialog.hide();
  }
}

export const portfolioAttributionReport: angular.IComponentOptions = {
  template: require("./portfolioAttributionReport.html"),
  controller: PortfolioAttributionReportController,
  bindings: {
    clientId: "<",
    selectedPortfolioIds: "<",
    selectedCurrency: "<",
    clientPortfolios: "<",
    client: "<",
    clientNumber: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/portfolioadmin/reportManagement/portfolioAttribution/portfolioattribution.report.ts